import React, { useState } from 'react';
import { Space, Tag, Modal, Tooltip, Alert, Radio, App, theme } from 'antd';
import { UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { FormModalFooter } from '@/components/ui/FormModalFooter';
import { getApiErrorMessage } from '@/utils/apiError';
import { RoleBadgeTrigger } from './RoleBadgeTrigger';
import { getRoleTokenStyles } from './roleTokenStyles';

/** One selectable role in the editor. */
export interface RoleEditorOption<Role extends string> {
  value: Role;
  label: string;
  color: string;
  description?: string;
  /** Optional icon on the role's Tag (e.g. a crown for owners). */
  icon?: React.ReactNode;
}

/** Translated copy shown by the editor; each wrapper supplies its own namespace. */
export interface RoleEditorTexts {
  tooltip: string;
  changeTitle: string;
  editingFor: string;
  selectNewRole: string;
  current: string;
  confirmTitle: string;
  /** Confirmation line for the newly selected role label. */
  confirmDescription: (roleLabel: string) => string;
  saveChanges: string;
  selectRole: string;
  noChanges: string;
  updateSuccess: string;
  updateError: string;
}

interface RoleEditorModalProps<Role extends string> {
  currentRole: Role;
  /** Roles offered in the modal. */
  options: RoleEditorOption<Role>[];
  /** Used for the badge and the confirmation, so the current role shows even when it is not offered. */
  findOption: (role: Role) => RoleEditorOption<Role> | undefined;
  /** Persists the new role; rejects on failure. */
  save: (role: Role) => Promise<unknown>;
  saving: boolean;
  texts: RoleEditorTexts;
  userName?: string;
  /** Shows a plain badge without opening the modal. */
  readOnly?: boolean;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

/**
 * Role badge that opens a modal to pick a new role, confirm it and save it.
 * Shared by the global user role editor and the tenant role editor.
 */
export function RoleEditorModal<Role extends string>({
  currentRole,
  options,
  findOption,
  save,
  saving,
  texts,
  userName,
  readOnly = false,
  onSuccess,
  onError
}: RoleEditorModalProps<Role>) {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const currentOption = findOption(currentRole);

  const handleOpenModal = () => {
    if (readOnly) return;
    setIsModalOpen(true);
    setSelectedRole(currentRole);
  };

  const handleCloseModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setSelectedRole(null);
  };

  const handleSaveRole = async () => {
    if (!selectedRole || !options.some((option) => option.value === selectedRole)) {
      message.error(texts.selectRole);
      return;
    }
    if (selectedRole === currentRole) {
      message.info(texts.noChanges);
      handleCloseModal();
      return;
    }
    try {
      await save(selectedRole);
      message.success(texts.updateSuccess);
      setIsModalOpen(false);
      setSelectedRole(null);
      onSuccess?.();
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, texts.updateError));
      onError?.(error);
    }
  };

  const selectedOption = findOption(selectedRole || currentRole);
  const hasChanges = selectedRole && selectedRole !== currentRole;

  /** Card-like radio style; the selected role gets a thicker border in its own colour. */
  const getRadioStyle = (option: RoleEditorOption<Role>): React.CSSProperties => {
    const isSelected = selectedRole === option.value;
    const borderColor = isSelected ? getRoleTokenStyles(option.color, token).border : token.colorBorder;
    return {
      width: '100%',
      padding: token.paddingSM,
      border: `${isSelected ? 2 : 1}px solid ${borderColor}`,
      borderRadius: token.borderRadius,
      background: isSelected ? token.colorFillTertiary : token.colorBgContainer,
      marginRight: 0
    };
  };

  return (
    <>
      <Tooltip title={texts.tooltip} placement="top">
        <span style={{ display: 'inline-block' }}>
          <RoleBadgeTrigger
            color={currentOption?.color}
            label={currentOption?.label || currentRole}
            onClick={handleOpenModal}
            readOnly={readOnly}
          />
        </span>
      </Tooltip>

      <Modal
        title={
          <Space>
            <UserOutlined />
            <span>{texts.changeTitle}</span>
          </Space>
        }
        open={isModalOpen}
        footer={null}
        onCancel={handleCloseModal}
        width={520}
        centered
      >
        <div style={{ padding: `${token.paddingXS}px 0` }}>
          {userName && (
            <div
              style={{
                marginBottom: token.marginLG,
                padding: token.paddingSM,
                background: token.colorFillTertiary,
                borderRadius: token.borderRadius,
                border: `1px solid ${token.colorBorderSecondary}`
              }}
            >
              <div style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: token.marginXXS }}>
                {texts.editingFor}
              </div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{userName}</div>
            </div>
          )}

          <div style={{ marginBottom: token.marginMD }}>
            <div style={{ marginBottom: token.marginSM }}>
              <span style={{ color: token.colorTextSecondary, fontWeight: 500 }}>{texts.selectNewRole}</span>
            </div>
            <Radio.Group
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              style={{ width: '100%' }}
              disabled={saving}
            >
              <Space orientation="vertical" style={{ width: '100%' }} size={12}>
                {options.map((option) => (
                  <Radio key={option.value} value={option.value} style={getRadioStyle(option)}>
                    <div style={{ marginLeft: token.marginXS }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: token.marginXS,
                          marginBottom: token.marginXXS
                        }}
                      >
                        <Tag color={option.color} icon={option.icon} style={{ margin: 0, fontSize: 13 }}>
                          {option.label}
                        </Tag>
                        {option.value === currentRole && (
                          <Tag color="default" icon={<CheckCircleOutlined />} style={{ fontSize: 11 }}>
                            {texts.current}
                          </Tag>
                        )}
                      </div>
                      {option.description && (
                        <div style={{ fontSize: 12, color: token.colorTextSecondary, lineHeight: 1.4 }}>
                          {option.description}
                        </div>
                      )}
                    </div>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </div>

          {hasChanges && selectedOption && (
            <Alert
              title={texts.confirmTitle}
              description={
                <div>
                  {texts.confirmDescription(selectedOption.label)}
                  <br />
                  <span style={{ fontSize: 12 }}>{selectedOption.description}</span>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginTop: token.marginMD }}
            />
          )}
        </div>
        <FormModalFooter
          onCancel={handleCloseModal}
          onSubmit={handleSaveRole}
          submitLabel={texts.saveChanges}
          loading={saving}
          submitDisabled={!selectedRole || selectedRole === currentRole}
        />
      </Modal>
    </>
  );
}
